import { useEffect, useLayoutEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { X } from "lucide-react";

const STEPS = [
  { path: "/dashboard", sel: '[data-testid="brand-logo"]', title: "Welcome to EV-IDEX", body: "This 60-second tour shows you where everything is. EV-IDEX analyses real market data, publishes AI predictions that are graded later, and lets you practise with virtual money. Nothing here is financial advice." },
  { path: "/dashboard", sel: '[data-testid="mode-switch"]', title: "Beginner / Advanced", body: "You're in Beginner mode: plain-English notes everywhere and advanced tools folded away. Switch to Advanced any time — nothing is ever removed." },
  { path: "/dashboard", sel: '[data-testid="explain-page-btn"]', title: "Explain this page", body: "Lost on any screen? This button explains every section of the page you're on, in plain English." },
  { path: "/dashboard", sel: '[data-testid="prediction-score-card"]', title: "Prediction Accuracy Score", body: "How often EV-IDEX's finished predictions were right, recalculated daily from a tamper-evident ledger. Tap ⓘ on any number for What / Why / How / What to do." },
  { path: "/coin/bitcoin", sel: '[data-testid="signal-report"]', title: "Your first analysis: Bitcoin", body: "This is a Signal Report: outlook (Bullish / Bearish / Neutral / Wait), confidence (how strongly indicators agree — not a profit probability), risk, and the evidence for and against." },
  { path: "/coin/bitcoin", sel: '[data-testid="meaning-box"]', title: "What does this mean for me?", body: "Every report ends with a plain-English reading and the price at which the idea would be wrong. Read 'reasons it could fail' before anything else." },
  { path: "/coin/bitcoin", sel: '[data-testid="paper-trade-card"]', title: "Practise with virtual money", body: "Try a paper trade — real prices, $100,000 of practice cash, zero risk. Every decision is saved in your Journal." },
  { path: "/alerts", sel: '[data-testid="alerts-tabs"]', title: "Alerts on your phone", body: "Set price or level alerts and connect your phone to be notified even when EV-IDEX is closed. Alerts inform — they never trade." },
  { path: "/learn", sel: '[data-testid="learn-page"]', title: "Learn Crypto", body: "Short lessons with quizzes, from 'what is cryptocurrency' to strategy backtesting. You can replay this tour from Settings any time." },
];

export const TOUR_KEY = "apx:tour-done";

export const Tour = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [i, setI] = useState(-1);
  const [rect, setRect] = useState(null);

  useEffect(() => {
    const start = () => setI(0);
    window.addEventListener("apx:start-tour", start);
    const stopOnSearch = () => { setI(-1); setRect(null); };  // user navigated from search: pause the tour instead of bouncing them back
    window.addEventListener("apx:pause-tour", stopOnSearch);
    if (user && !user.tour_done && !localStorage.getItem(TOUR_KEY) && (user.ui_mode || "beginner") === "beginner" && pathname.startsWith("/dashboard")) setTimeout(start, 1200);
    return () => { window.removeEventListener("apx:start-tour", start); window.removeEventListener("apx:pause-tour", stopOnSearch); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const step = i >= 0 ? STEPS[i] : null;
  useEffect(() => { if (step && !pathname.startsWith(step.path)) nav(step.path); }, [step, pathname, nav]);

  useLayoutEffect(() => {
    if (!step) return;
    let tries = 0;
    const find = () => {
      const el = document.querySelector(step.sel);
      if (el) { el.scrollIntoView({ block: "center", behavior: "smooth" }); setTimeout(() => setRect(el.getBoundingClientRect()), 350); }
      else if (tries++ < 30) setTimeout(find, 300);
      else setRect(null);
    };
    find();
  }, [step, pathname]);

  const finish = () => { setI(-1); setRect(null); localStorage.setItem(TOUR_KEY, "1"); api.post("/auth/me/tour?done=true").catch(() => {}); };
  if (!step) return null;
  const pad = 8;
  const top = rect ? Math.min(window.innerHeight - 220, rect.bottom + 12) : 120;
  return (
    <div className="fixed inset-0 z-[100]" data-testid="tour-overlay">
      <div className="absolute inset-0 bg-black/60" onClick={finish} />
      {rect && <div className="absolute rounded-xl ring-2 ring-cyan-400 shadow-[0_0_0_9999px_rgba(0,0,0,0.6)] pointer-events-none" style={{ top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 }} />}
      <div className="absolute left-1/2 -translate-x-1/2 w-[92vw] max-w-md apx-card border border-cyan-900/60 p-4 shadow-2xl" style={{ top }} data-testid="tour-card">
        <div className="flex items-start justify-between gap-3">
          <div><div className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500">Step {i + 1} of {STEPS.length}</div><div className="font-display font-semibold mt-0.5" data-testid="tour-title">{step.title}</div></div>
          <button onClick={finish} aria-label="Close tour" data-testid="tour-close" className="btn-ghost rounded-md p-1"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-sm text-slate-300 mt-2 leading-relaxed" data-testid="tour-body">{step.body}</p>
        <div className="flex items-center justify-between mt-3">
          <button onClick={finish} className="text-xs text-slate-400 hover:text-slate-200" data-testid="tour-skip">Skip tour</button>
          <div className="flex gap-2">
            {i > 0 && <button onClick={() => setI(i - 1)} className="btn-ghost rounded-lg px-3 py-1.5 text-xs border border-slate-700" data-testid="tour-back">Back</button>}
            <button onClick={() => (i + 1 < STEPS.length ? setI(i + 1) : finish())} className="btn-primary rounded-lg px-3 py-1.5 text-xs font-semibold" data-testid="tour-next">{i + 1 < STEPS.length ? "Next" : "Finish"}</button>
          </div>
        </div>
      </div>
    </div>
  );
};
